import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { CustomButtonGroup, HighchartBarChart } from '../../../shared';
import PerformanceSkeleton from '../monthlyPrescriptionPerformance/skeleton';
import { createChartData, sortDataWithMonthYear } from '../../../../utility';
import { actions as webMatrixActions } from '../../../../redux/reducers/webMatrixReducer';

const WEBSITE_OPTIONS = [
    {
        label: 'Sessions', 
        definition: 'Total visits and interactions a user takes on the landing site.' 
    }, 
    {
        label: 'Quality Visits',
        definition: 'Quality Visits defined as the audience or people who download any material, visit the website for >30 seconds, clicks 2 or more times on the website which includes videos or navigation, signed up on Website through submitted form on "Learn More" tab.' 
    }
];

const DtcWebsitePerformance = () => {
    const dispatch = useDispatch();
    const { webMatrixData, isLoading } = useSelector(state => state.webMatrix);
    const { company } = useSelector(state => state.company);
    const { startDateRange, endDateRange } = useSelector(state => state.filter);
    const { resetToInitialState } = webMatrixActions;
    const [selectedWebsiteOption, setSelectedWebsiteOption] = useState(WEBSITE_OPTIONS[0].label);
    const [data, setData] = useState({
        Sessions: {
            xAxisCategories: [],
            dataSets: [],
            colors: []
        },
        'Quality Visits': {
            xAxisCategories: [],
            dataSets: [],
            colors: []
        }
    });

    const handleWebsitePerformance = (option) => {
        setSelectedWebsiteOption(option);
    };

    const handleChartOptions = (selectedOption, colors = [], xAxisCategories = []) => {
        const xAxisLabels = xAxisCategories?.length > 0 ? sortDataWithMonthYear(xAxisCategories) : [];
    
        return {
            title: { text: null },
            subtitle: { text: null },
            chart: {
                backgroundColor: 'transparent',
                type: 'column',
                zoomType: 'none',
                panning: false
            },
            credits: { enabled: false },
            exporting: { enabled: false }, 
            colors: ['#AD509E'],
            navigation: { buttonOptions: { enabled: false } },
            tooltip: {
                enabled: true,
                headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
                pointFormat: '<span style="font-size: 12px">{series.name}: <b>{point.y}</b><br/></span>'
            },
            legend: { enabled: false },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderRadius: 3,
                    maxPointWidth: 50
                }
            },
            xAxis: {
                categories: xAxisLabels,
                title: { text: 'Months' },
                lineColor: 'transparent'
            },
            yAxis: {
                min: 0,
                title: { text: '' },
                stackLabels: { enabled: false },
                lineColor: 'transparent',
                gridLineWidth: 0
            }
        };
    };
    

    const handleWebMatrixData = () => {
        if (webMatrixData?.sessions_chart_data?.length > 0) {
            const chartData = createChartData(webMatrixData.sessions_chart_data, 'sessions_value', true);
            if (chartData?.xAxisCategories?.length > 0) {
                setData(prevState => ({
                    ...prevState,
                    Sessions: {
                        xAxisCategories: chartData.xAxisCategories,
                        dataSets: chartData.dataSets,
                        colors: chartData.colors || ['#AD509E']
                    }
                }));
            }
        }

        if (selectedWebsiteOption === 'Quality Visits') {
            const chartData = createChartData(webMatrixData.quality_visits_chart_data, 'quality_visits_value', true);
            if (chartData?.xAxisCategories?.length > 0) {
                setData(prevState => ({
                    ...prevState,
                    'Quality Visits': {
                        xAxisCategories: chartData.xAxisCategories,
                        dataSets: chartData.dataSets,
                        colors: chartData.colors || ['#AD509E']
                    }
                }));
            }
        }
    };

    useEffect(() => {
        if (webMatrixData && Object.keys(webMatrixData).length > 0) {
            handleWebMatrixData();
        }
    }, [webMatrixData, selectedWebsiteOption]);
    useEffect(() => {
        dispatch(resetToInitialState());
    }, [company, startDateRange, endDateRange]);
    return (
        <Box>
            <Typography className="text-lg-semi-bold gray-900">
                Website Performance
            </Typography>
            {isLoading ? (
                <PerformanceSkeleton />
            ) : (
                <>
                    <Box className="margin-tb-20">
                        <CustomButtonGroup
                            data={WEBSITE_OPTIONS}
                            handleClick={handleWebsitePerformance}
                            isToolTip={true}
                            key="monthly-prescription-performance"
                            selected={selectedWebsiteOption}
                        />
                    </Box>
                    <Box>
                        <HighchartBarChart
                            chartOptions={handleChartOptions(
                                selectedWebsiteOption,
                                data[selectedWebsiteOption]?.colors || ['#AD509E'],
                                data[selectedWebsiteOption]?.xAxisCategories || []
                            )}
                            seriesData={data[selectedWebsiteOption]?.dataSets || []}
                            seriesName={selectedWebsiteOption}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default DtcWebsitePerformance;
